import { ConfirmDialog, confirmDialog } from "primereact/confirmdialog";
import React, { createContext, ReactNode, useContext, useEffect, useState } from "react";
import './AuthContext.css'
import { useNavigate } from "react-router-dom";
import { useAppDispatch } from "../Controllers/hooks";
import { goToTop } from "../Components/goToTop";

interface AuthContextType {
    isAuthenticated: boolean;
    loading: boolean;
    login: (token: string) => void;
    logout: () => void;
    reLogin: () => void;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const useAuth = () => {
    const context = useContext(AuthContext);

    if (!context) {
        throw new Error('useAuth must be used within AuthProvider');
    }

    return context;
}

export const AuthProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
    const [isAuthenticated, setIsAuthenticated] = useState<boolean>(false);
    const [loading, setLoading] = useState<boolean>(true);
    const navigate = useNavigate();
    const dispatch = useAppDispatch();

    useEffect(() => {
        const loadData = async () => {
            try {
                const token = localStorage.getItem('authToken');
                const user_type_id = localStorage.getItem('user_type_id');

                if (token) {
                    setIsAuthenticated(true);

                    const { getAllPermissionsThunk } = await import('../Controllers/Thunks/permissionThunks');
                    dispatch(getAllPermissionsThunk(Number(user_type_id)));

                    const { fetchAllContact } = await import('../Controllers/Thunks/contactThunks');
                    dispatch(fetchAllContact());
                } else {
                    setIsAuthenticated(false);
                    navigate('/auth/login'); // Redirect to login on error
                }
            } catch (error) {
                console.error('Error loading data:', error);
                setIsAuthenticated(false);
                navigate('/auth/login'); // Redirect to login on error
            } finally {
                setLoading(false);
            }
        }

        loadData();
    }, []);

    const login = (token: string) => {
        localStorage.setItem('authToken', token);
        setIsAuthenticated(true);

    }

    const logout = () => {
        //setShowLogoutDialog(true);
        confirmDialog({
            message: 'Are you sure you want to log out?',
            header: 'Logout Confirmation',
            icon: 'bi bi-info-circle',
            defaultFocus: 'accept',
            acceptClassName: 'p-button-danger',
            dismissableMask: true,
            resizable: false,
            draggable: false,
            accept: () => {
                localStorage.removeItem('authToken');
                localStorage.removeItem('token');
                localStorage.removeItem('user_id');
                localStorage.removeItem('user_type_id');
                localStorage.removeItem('user_type_name');
                localStorage.removeItem('company_name');
                localStorage.removeItem('currency_symbol');
                setIsAuthenticated(false);

                // Reset all redux state
                dispatch({ type: 'RESET' });
                
                navigate('/auth/login');
            }
        });
        goToTop();
    }

    const reLogin = () => {
        confirmDialog({
            message: 'Network error, login again!',
            header: 'Re-login',
            icon: 'bi bi-info-circle',
            defaultFocus: 'accept',
            acceptClassName: 'p-button-danger',
            rejectClassName: 'd-none',
            acceptLabel: 'Ok',
            closable: false,
            resizable: false,
            draggable: false,
            accept: () => {
                localStorage.removeItem('authToken');
                localStorage.removeItem('token');
                localStorage.removeItem('user_id');
                localStorage.removeItem('user_type_id');
                localStorage.removeItem('user_type_name');
                localStorage.removeItem('company_name');
                localStorage.removeItem('currency_symbol');
                setIsAuthenticated(false);
            },

        });


    };

    return (
        <AuthContext.Provider value={{ isAuthenticated, loading, login, logout, reLogin }}>
            {children}
            <ConfirmDialog />
        </AuthContext.Provider>
    )
}